import { lazy, Suspense } from "react";
import {
  HandThumbUpIcon,
  HandThumbDownIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { AnyMessage, AssistantMessage } from "./types";
import SourceBadges from "./Badge";
import FilloutFormMessage from "./forms";
import { CopyButton } from "./CopyButton";
import { useAppDispatch, useAppSelector } from "../store";
import { useCurrentFrameSettings } from "../hooks";
import { rateMessage } from "../actions/sharedActions";
import LiveFragment from "./LiveFragment";
import { useFramePubSub } from "../pubsub";
import { HandoffMessageItem } from "./HandoffMessageItem";
import { FileData } from "../types/api";
import { FileMessageItem } from "./FileMessageItem";

const MarkdownRenderer = lazy(() => import("./Markdown"));

// Form-type message
const FormMessageItem = ({ metadata }: { metadata: any }) => (
  <div aria-live="polite">
    <Suspense fallback={<div>Form loading...</div>}>
      <FilloutFormMessage filloutId={metadata.form.filloutId} />
    </Suspense>
  </div>
);

// HTML-type message
const HtmlMessageItem = ({ metadata }: { metadata: any }) => {
  return (
    <div aria-live="polite">
      <div className="w-full max-w-xl mx-auto">
        <LiveFragment html={metadata.html} />
      </div>
    </div>
  );
};

// Message rating controls
function MessageControls({ message }: { message: AssistantMessage }) {
  const dispatch = useAppDispatch();
  const { publish } = useFramePubSub();
  const conversationId = useAppSelector((state) => state.chat.conversationId)!;
  const rate = (rating: string) => {
    const func = async () => {
      await dispatch(
        rateMessage({
          messageId: message.message_id,
          rating,
        })
      );
      publish({
        type: "message_rating",
        conversation_id: conversationId,
        message_id: message.message_id,
        rating,
      });
    };
    func();
  };
  return (
    <div>
      <SourceBadges sources={message.metadata?.sources ?? []} />
      <div className="flex items-center gap-3 justify-start p-[1px]">
        <CopyButton
          className="h-5 w-5 hover:text-brand-hover opacity-60 "
          text={message.content}
        />
        <button
          className="hover:text-brand-hover disabled:text-brand disabled:opacity-100 focus:outline-brand"
          disabled={message.rating === "thumbsUp" || message.isGenerating}
          onClick={() => rate("thumbsUp")}
          aria-label="Rate message thumbs up"
        >
          <HandThumbUpIcon className="h-5 w-5 opacity-60 " />
        </button>
        <button
          className="hover:text-brand-hover disabled:text-brand disabled:opacity-100 focus:outline-brand"
          disabled={message.rating === "thumbsDown" || message.isGenerating}
          onClick={() => rate("thumbsDown")}
          aria-label="Rate message thumbs down"
        >
          <HandThumbDownIcon className="h-5 w-5 opacity-60 " />
        </button>
      </div>
    </div>
  );
}

// Assistant message
const AssistantMessageItem = ({ message }: { message: AssistantMessage }) => {
  const { t } = useTranslation();
  const { data } = useCurrentFrameSettings();
  const handoff = message.metadata?.handoff;
  const hasHandoffOffer = handoff != null;

  return (
    <div className="w-full flex gap-2" aria-live="polite">
      {data?.assistant_image_url && (
        <img
          src={data.assistant_image_url}
          alt="Assistant"
          className="w-8 h-8 rounded-full flex-shrink-0 object-cover mt-[4px]"
        />
      )}
      <div className="flex-1 min-w-0">
        <span className="text-sm text-brand">
          {data?.assistant_name ?? t("assistant")}
        </span>
        <div className="prose text-xm prose-img:rounded max-w-full min-w-0 overflow-x-auto no-scrollbar">
          <Suspense fallback={<div>{message.content}</div>}>
            <MarkdownRenderer content={message.content} />
          </Suspense>
          {!message.isGenerating && hasHandoffOffer && (
            <div>
              {hasHandoffOffer && <HandoffMessageItem offer={handoff} />}
            </div>
          )}
          {!message.isInitial &&
            message.sender === "assistant" &&
            !message.isGenerating &&
            !hasHandoffOffer && <MessageControls message={message} />}
        </div>
      </div>
      {/* <div>
        {!hasHandoffOffer &&
          extractMarkdownUrls(message.content)
            .slice(0, 1)
            .map((url) => <LinkPreview url={url} key={url} />)}
      </div> */}
    </div>
  );
};

const UserMessageItem = ({
  content,
  attachments,
}: {
  content: string;
  attachments: FileData[];
}) => {
  const hasAttachments = attachments && attachments.length > 0;

  return (
    <div className="w-full">
      <div
        aria-live="polite"
        className="flex flex-col items-end gap-1 max-w-full justify-end"
      >
        {/* File attachments preview */}
        {hasAttachments && <FileMessageItem files={attachments} />}

        {/* Message content */}
        {content.trim() && (
          <div
            className={`prose prose-sm text-chat bg-brand py-1.5 px-4 flex-shrink-0 float-right rounded-bl-[24px] rounded-tl-[24px] rounded-tr-[24px] ${hasAttachments ? "mt-1" : ""}`}
          >
            <p className="whitespace-pre-wrap break-words">{content.trim()}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Main message component that determines which subtype to render
export function MessageItem({ message }: { message: AnyMessage }) {
  const { role, metadata } = message;

  if (metadata?.hasOwnProperty("form")) {
    return <FormMessageItem metadata={metadata} />;
  }

  if (metadata?.hasOwnProperty("html")) {
    return <HtmlMessageItem metadata={metadata} />;
  }

  if (role === "assistant") {
    return <AssistantMessageItem message={message} />;
  }

  if (role === "user") {
    return (
      <UserMessageItem
        content={message.content}
        attachments={message.attachments}
      />
    );
  }

  return <div>Unknown role: {role}</div>;
}
